import { useEffect, useState } from 'react';
import './WorkerDocuments.css';
import { fold } from '../../utils/zoek';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('ozel_token');
  return fetch(url, {
    ...options,
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    credentials: 'include',
  });
}

const VIES = {
  geldig: { label: 'Btw geldig', cls: 'ok' },
  naam_afwijkend: { label: 'Btw geldig, naam wijkt af', cls: 'warn' },
  ongeldig: { label: 'Btw ongeldig', cls: 'bad' },
  fout: { label: 'Btw niet gecontroleerd', cls: 'warn' },
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

// Documenten die medewerkers zelf uploaden via een registratielink (ID, KvK, certificaten).
export default function WorkerDocuments() {
  const [groups, setGroups] = useState(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(() => new Set());
  const [error, setError] = useState('');
  const [viesBusy, setViesBusy] = useState(null);
  const [viesMsg, setViesMsg] = useState({});

  const load = () => {
    authFetch('/api/registratie/files')
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        setGroups(d);
        setOpen((o) => (o.size ? o : new Set(d.filter((g) => g.nieuw).map((g) => g.workerId))));
      })
      .catch(() => setError('Medewerkerdocumenten laden mislukt.'));
  };
  useEffect(load, []);

  const toggle = (id) => setOpen((o) => {
    const n = new Set(o);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const markChecked = async (g) => {
    const r = await authFetch(`/api/registratie/workers/${g.workerId}/gecontroleerd`, { method: 'POST' });
    if (r.ok) setGroups((list) => list.map((x) => (x.workerId === g.workerId ? { ...x, nieuw: false } : x)));
  };

  const recheck = async (g) => {
    setViesBusy(g.workerId);
    setViesMsg((m) => ({ ...m, [g.workerId]: '' }));
    try {
      const r = await authFetch(`/api/registratie/workers/${g.workerId}/vies`, { method: 'POST' });
      const d = await r.json();
      if (d.status === 'fout') setViesMsg((m) => ({ ...m, [g.workerId]: `VIES: ${d.error || 'niet bereikbaar'}. Probeer het later opnieuw.` }));
      load();
    } catch {
      setViesMsg((m) => ({ ...m, [g.workerId]: 'Controle mislukt.' }));
    } finally {
      setViesBusy(null);
    }
  };

  const q = fold(query).trim();
  const shown = (groups || []).filter((g) => !q || fold(g.workerName).includes(q));
  const newCount = (groups || []).filter((g) => g.nieuw).length;

  return (
    <section className="wd">
      <div className="wd-head">
        <div>
          <h3>Medewerkerdocumenten</h3>
          <p>ID, KvK-uittreksel en certificaten die medewerkers zelf uploaden via een registratielink.</p>
        </div>
        {newCount > 0 && <span className="wd-new-count">{newCount} nieuw</span>}
      </div>

      {error && <p className="wd-error">{error}</p>}

      {groups && groups.length > 0 && (
        <input
          className="wd-search"
          type="search"
          placeholder="Zoek medewerker…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      )}

      {groups === null && !error && <p className="wd-muted">Laden…</p>}
      {groups && groups.length === 0 && (
        <p className="wd-muted wd-empty">Nog geen documenten. Ze verschijnen hier zodra iemand een registratielink invult.</p>
      )}

      <ul className="wd-list">
        {shown.map((g) => (
          <li key={g.workerId} className={`wd-item ${g.nieuw ? 'is-new' : ''}`}>
            <button type="button" className="wd-row" onClick={() => toggle(g.workerId)} aria-expanded={open.has(g.workerId)}>
              <span className="wd-name">{g.workerName}</span>
              {g.nieuw && <span className="wd-badge">Nieuw</span>}
              {VIES[g.viesStatus] && <span className={`wd-vies ${VIES[g.viesStatus].cls}`}>{VIES[g.viesStatus].label}</span>}
              <span className="wd-meta">{g.files.length} bestand{g.files.length === 1 ? '' : 'en'} · {fmtDate(g.createdAt)}</span>
              <span className="wd-caret">{open.has(g.workerId) ? '−' : '+'}</span>
            </button>
            {open.has(g.workerId) && (
              <div className="wd-body">
                <ul className="wd-files">
                  {g.files.map((f) => (
                    <li key={f.id}>
                      <span className={`wd-type ${f.mimeType === 'application/pdf' ? 'pdf' : 'img'}`}>
                        {f.mimeType === 'application/pdf' ? 'PDF' : 'FOTO'}
                      </span>
                      <span className="wd-file-label">{f.label}</span>
                      <a href={`/api/registratie/files/${f.id}`} target="_blank" rel="noopener noreferrer">Bekijken</a>
                      <a href={`/api/registratie/files/${f.id}?download=1`}>Downloaden</a>
                    </li>
                  ))}
                </ul>
                <div className="wd-vies-row">
                  <span>Btw-nummer: <b>{g.btwNumber || '—'}</b></span>
                  <button type="button" className="wd-vies-btn" disabled={viesBusy === g.workerId || !g.btwNumber} onClick={() => recheck(g)}>
                    {viesBusy === g.workerId ? 'Controleren bij VIES…' : 'Btw controleren (VIES)'}
                  </button>
                </div>
                {viesMsg[g.workerId] && <p className="wd-error">{viesMsg[g.workerId]}</p>}
                {g.nieuw && (
                  <button type="button" className="wd-check" onClick={() => markChecked(g)}>
                    Gecontroleerd
                  </button>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
